/**
 * Carrier Portal (PR5)
 *
 * Landing + per-delivery view for the carrier-adjuster seat.
 * Court-exhibit layout; no strategy/ledger/what-if UI.
 * No usePermissions — the backend gates every endpoint.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Loader2,
  MessageSquare,
  Package,
  Send,
  Upload,
} from 'lucide-react';
import {
  listDeliveries,
  getDelivery,
  downloadArtifact,
  ack,
  postInbound,
  postRfi,
  getThread,
  postPosition,
  type CarrierDeliveryListItem,
  type CarrierDeliveryResponse,
  type CarrierThreadMessage,
} from '../services/carrierApi';
import { FinancialValue } from '../components/shared/FinancialValue';


function firstFigures(artifacts: { figures_as_rendered?: Record<string, unknown> | null }[] | undefined | null) {
  const hit = artifacts?.find((a) => a.figures_as_rendered && typeof a.figures_as_rendered === 'object');
  return hit?.figures_as_rendered ?? null;
}

function formatDate(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return d;
  }
}

type TabKey = 'manifest' | 'rfi' | 'position';

export default function CarrierPortal() {
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const navigate = useNavigate();

  const [deliveries, setDeliveries] = useState<CarrierDeliveryListItem[] | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [errorList, setErrorList] = useState<string | null>(null);

  const [delivery, setDelivery] = useState<CarrierDeliveryResponse | null>(null);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [errorDelivery, setErrorDelivery] = useState<string | null>(null);

  const [tab, setTab] = useState<TabKey>('manifest');
  const [thread, setThread] = useState<CarrierThreadMessage[]>([]);
  const [rfiText, setRfiText] = useState('');
  const [sendingRfi, setSendingRfi] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [positionKind, setPositionKind] = useState('accept');
  const [positionAmount, setPositionAmount] = useState('');
  const [positionLines, setPositionLines] = useState<
    { label: string; figure_key: string; amount: string; status: string; reason: string }[]
  >([]);
  const [postingPosition, setPostingPosition] = useState(false);

  const isListView = !deliveryId;

  // Load delivery list
  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    listDeliveries()
      .then((res) => {
        if (cancelled) return;
        setDeliveries(res.deliveries);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setErrorList(e?.message || 'Failed to load deliveries');
      })
      .finally(() => setLoadingList(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load single delivery
  useEffect(() => {
    if (!deliveryId) {
      setDelivery(null);
      setThread([]);
      return;
    }
    let cancelled = false;
    setLoadingDelivery(true);
    setErrorDelivery(null);
    setTab('manifest');

    Promise.all([
      getDelivery(deliveryId).catch(() => null),
      getThread(deliveryId).catch(() => null),
    ])
      .then(([delRes, threadRes]) => {
        if (cancelled) return;
        setDelivery(delRes);
        setThread(threadRes?.messages ?? []);
        // Seed position lines from figures_as_rendered if present
        const figs = firstFigures(delRes?.artifacts);
        if (figs && typeof figs === 'object') {
          const lines = Object.entries(figs).map(([k, v]) => ({
            label: k,
            figure_key: k,
            amount: typeof v === 'number' ? String(v) : '',
            status: 'accept',
            reason: '',
          }));
          setPositionLines(lines);
        } else {
          setPositionLines([]);
        }
      })
      .catch((e: any) => {
        if (cancelled) return;
        setErrorDelivery(e?.message || 'Failed to load delivery');
      })
      .finally(() => setLoadingDelivery(false));

    return () => {
      cancelled = true;
    };
  }, [deliveryId]);

  const handleAck = useCallback(async () => {
    if (!deliveryId) return;
    setAckLoading(true);
    try {
      await ack(deliveryId);
      const refreshed = await getDelivery(deliveryId);
      setDelivery(refreshed);
    } catch (e: any) {
      setErrorDelivery(e?.message || 'Ack failed');
    } finally {
      setAckLoading(false);
    }
  }, [deliveryId]);

  const handleUploadInbound = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!deliveryId || !e.target.files?.[0]) return;
      setUploading(true);
      try {
        await postInbound(deliveryId, e.target.files[0]);
        const refreshed = await getDelivery(deliveryId);
        setDelivery(refreshed);
      } catch (e: any) {
        setErrorDelivery(e?.message || 'Upload failed');
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [deliveryId],
  );

  const handlePostRfi = useCallback(async () => {
    if (!deliveryId || !rfiText.trim()) return;
    setSendingRfi(true);
    try {
      await postRfi(deliveryId, { message: rfiText.trim() });
      const refreshed = await getThread(deliveryId);
      setThread(refreshed.messages);
      setRfiText('');
    } catch (e: any) {
      setErrorDelivery(e?.message || 'RFI failed');
    } finally {
      setSendingRfi(false);
    }
  }, [deliveryId, rfiText]);

  const handlePostPosition = useCallback(async () => {
    if (!deliveryId) return;
    setPostingPosition(true);
    try {
      await postPosition(deliveryId, {
        kind: positionKind,
        amount: positionAmount ? Number(positionAmount) : null,
        lines: positionLines.map((l) => ({
          label: l.label,
          figure_key: l.figure_key || null,
          amount: l.amount ? Number(l.amount) : null,
          status: l.status,
          reason: l.reason,
        })),
      });
      alert('Position submitted');
    } catch (e: any) {
      setErrorDelivery(e?.message || 'Position failed');
    } finally {
      setPostingPosition(false);
    }
  }, [deliveryId, positionKind, positionAmount, positionLines]);

  const updateLine = (idx: number, patch: Partial<(typeof positionLines)[number]>) => {
    setPositionLines((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    );
  };

  if (isListView) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }}>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="cs-display mb-6">
            Carrier Portal
          </h1>
          {loadingList && (
            <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading deliveries…
            </div>
          )}
          {errorList && (
            <div
              className="p-4 rounded-lg border"
              style={{
                backgroundColor: 'var(--color-blocker-tint)',
                borderColor: 'var(--color-blocker-tint-bd)',
                color: 'var(--color-blocker)',
              }}
            >
              {errorList}
            </div>
          )}
          {!loadingList && deliveries && deliveries.length === 0 && (
            <p style={{ color: 'var(--color-text-secondary)' }}>No deliveries.</p>
          )}
          <div className="grid gap-3">
            {deliveries?.map((d) => (
              <button
                key={d.delivery_id}
                onClick={() => navigate(`/carrier/delivery/${d.delivery_id}`)}
                className="text-left p-4 rounded-lg border transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-surface)',
                  borderColor: 'var(--color-border)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-surface)';
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      Delivery {d.delivery_id?.slice(-6)}
                    </div>
                    <div className="text-sm mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                      Case {d.case_id?.slice(-6)} • Delivered {formatDate(d.delivered_at)}
                    </div>
                  </div>
                  {d.acknowledged_at ? (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full border"
                      style={{
                        borderColor: 'var(--color-success-tint-bd)',
                        color: 'var(--color-success)',
                      }}
                    >
                      Acknowledged
                    </span>
                  ) : (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full border"
                      style={{
                        borderColor: 'var(--color-needs-human-tint-bd)',
                        color: 'var(--color-needs-human-text)',
                      }}
                    >
                      Pending ack
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Delivery detail view
  const figures = firstFigures(delivery?.artifacts);
  const figureEntries = figures && typeof figures === 'object' ? Object.entries(figures) : [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/carrier')}
          className="flex items-center gap-1 text-sm mb-4"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to deliveries
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="cs-h2">
            Delivery {deliveryId?.slice(-6)}
          </h1>
          <div className="flex items-center gap-2">
            {delivery?.acknowledged_at ? (
              <span
                className="text-xs px-2 py-1 rounded-full border flex items-center gap-1"
                style={{
                  borderColor: 'var(--color-success-tint-bd)',
                  color: 'var(--color-success)',
                }}
              >
                <CheckCircle className="w-3 h-3" />
                Acknowledged {formatDate(delivery.acknowledged_at)}
              </span>
            ) : (
              <button
                onClick={handleAck}
                disabled={ackLoading}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--color-brand)',
                  color: '#fff',
                  opacity: ackLoading ? 0.6 : 1,
                }}
              >
                <CheckCircle className="w-4 h-4" />
                {ackLoading ? 'Saving…' : 'Acknowledge'}
              </button>
            )}
          </div>
        </div>

        {errorDelivery && (
          <div
            className="p-3 rounded-lg border mb-4 text-sm"
            style={{
              backgroundColor: 'var(--color-blocker-tint)',
              borderColor: 'var(--color-blocker-tint-bd)',
              color: 'var(--color-blocker)',
            }}
          >
            {errorDelivery}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {(['manifest', 'rfi', 'position'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-3 py-2 rounded-lg text-sm capitalize transition-colors"
              style={{
                backgroundColor: tab === t ? 'var(--color-bg-elevated)' : 'transparent',
                color: tab === t ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                border:
                  tab === t ? '1px solid var(--color-border)' : '1px solid transparent',
              }}
            >
              {t === 'manifest' ? 'Manifest' : t}
            </button>
          ))}
        </div>

        {loadingDelivery && (
          <div className="flex items-center gap-2 py-8" style={{ color: 'var(--color-text-secondary)' }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading delivery…
          </div>
        )}

        {!loadingDelivery && tab === 'manifest' && (
          <div className="space-y-4">
            {/* Figures as rendered */}
            <div
              className="rounded-lg border"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              <div
                className="px-4 py-3 text-sm font-medium"
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              >
                Figures as delivered {formatDate(delivery?.delivered_at)}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th
                      className="text-left px-4 py-2 font-medium"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      Label
                    </th>
                    <th
                      className="text-right px-4 py-2 font-medium"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {figureEntries.map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                      <td className="px-4 py-2" style={{ color: 'var(--color-text-primary)' }}>
                        {k}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <FinancialValue
                          value={typeof v === 'number' || typeof v === 'string' ? v : null}
                        />
                      </td>
                    </tr>
                  ))}
                  {figureEntries.length === 0 && (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-4 py-6 text-center"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        No figures.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Artifacts */}
            <div
              className="rounded-lg border"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              <div
                className="px-4 py-3 text-sm font-medium"
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              >
                Artifacts
              </div>
              <div className="grid gap-2 p-3">
                {delivery?.artifacts?.map((a, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 rounded border"
                    style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border-sub)' }}
                  >
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                      <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {a.type || 'Artifact'}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {a.file_key?.slice(-20)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-sm"
                      style={{ color: 'var(--color-brand)' }}
                      onClick={async () => {
                        try {
                          const blob = await downloadArtifact(deliveryId!, idx);
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = a.file_key?.split('/').pop() || a.type || `artifact-${idx}`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          console.error('artifact download failed', e);
                        }
                      }}
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                ))}
                {!delivery?.artifacts?.length && (
                  <p className="text-sm py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    No artifacts.
                  </p>
                )}
              </div>
            </div>

            {/* Inbound upload */}
            <div className="flex items-center gap-3">
              <label
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer"
                style={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading…' : 'Upload inbound'}
                <input type="file" className="hidden" onChange={handleUploadInbound} disabled={uploading} />
              </label>
            </div>
          </div>
        )}

        {!loadingDelivery && tab === 'rfi' && (
          <div className="space-y-4">
            <div
              className="rounded-lg border"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              <div
                className="px-4 py-3 text-sm font-medium flex items-center gap-2"
                style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                <MessageSquare className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                RFI Thread
              </div>
              <div className="p-3 space-y-2">
                {thread.map((m, i) => (
                  <div
                    key={i}
                    className="p-3 rounded border"
                    style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border-sub)' }}
                  >
                    <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {m.text}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      {m.sender} • {formatDate(m.created_at)}
                    </div>
                  </div>
                ))}
                {thread.length === 0 && (
                  <p className="text-sm py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    No messages.
                  </p>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              <textarea
                value={rfiText}
                onChange={(e) => setRfiText(e.target.value)}
                placeholder="Ask a question…"
                rows={3}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none"
                style={{
                  backgroundColor: 'var(--color-bg-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              />
              <div className="flex justify-end">
                <button
                  onClick={handlePostRfi}
                  disabled={sendingRfi || !rfiText.trim()}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-brand)',
                    color: '#fff',
                    opacity: sendingRfi || !rfiText.trim() ? 0.6 : 1,
                  }}
                >
                  <Send className="w-4 h-4" />
                  {sendingRfi ? 'Sending…' : 'Send RFI'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!loadingDelivery && tab === 'position' && (
          <div className="space-y-4">
            <div
              className="rounded-lg border"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              <div
                className="px-4 py-3 text-sm font-medium"
                style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                Carrier Position
              </div>
              <div className="p-3 space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Overall:
                  </label>
                  <select
                    value={positionKind}
                    onChange={(e) => setPositionKind(e.target.value)}
                    className="px-2 py-1 rounded text-sm border"
                    style={{
                      backgroundColor: 'var(--color-bg-base)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <option value="accept">Accept</option>
                    <option value="questioned">Questioned</option>
                    <option value="denied">Denied</option>
                  </select>
                  <input
                    value={positionAmount}
                    onChange={(e) => setPositionAmount(e.target.value)}
                    placeholder="Carrier figure"
                    className="px-2 py-1 rounded text-sm border outline-none"
                    style={{
                      backgroundColor: 'var(--color-bg-base)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                </div>

                {positionLines.map((line, idx) => (
                  <div
                    key={idx}
                    className="grid gap-2 p-3 rounded border"
                    style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border-sub)' }}
                  >
                    <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {line.label}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={line.status}
                        onChange={(e) => updateLine(idx, { status: e.target.value })}
                        className="px-2 py-1 rounded text-sm border"
                        style={{
                          backgroundColor: 'var(--color-bg-surface)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        <option value="accept">Accept</option>
                        <option value="questioned">Questioned</option>
                        <option value="denied">Denied</option>
                      </select>
                      <input
                        value={line.amount}
                        onChange={(e) => updateLine(idx, { amount: e.target.value })}
                        placeholder="Carrier figure"
                        className="px-2 py-1 rounded text-sm border outline-none"
                        style={{
                          backgroundColor: 'var(--color-bg-surface)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                        }}
                      />
                      <input
                        value={line.reason}
                        onChange={(e) => updateLine(idx, { reason: e.target.value })}
                        placeholder="Reason"
                        className="flex-1 min-w-[12rem] px-2 py-1 rounded text-sm border outline-none"
                        style={{
                          backgroundColor: 'var(--color-bg-surface)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                        }}
                      />
                    </div>
                  </div>
                ))}

                {positionLines.length === 0 && (
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    No figure lines to position. Upload an inbound or wait for a delivery with figures.
                  </p>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={handlePostPosition}
                    disabled={postingPosition}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'var(--color-brand)',
                      color: '#fff',
                      opacity: postingPosition ? 0.6 : 1,
                    }}
                  >
                    <Send className="w-4 h-4" />
                    {postingPosition ? 'Submitting…' : 'Submit position'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
